interface DateRecord {
  recordDate: string | Date;
}

/**
 * Generates a random string of specified length
 * @param len - Length of the string to generate
 * @returns Random string of specified length
 */
export const getRandomStr = (len: number): string => {
  if (len < 1) throw new Error('Length must be greater than 0')

  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  return Array.from(
    { length: len },
    () => possible.charAt(Math.floor(Math.random() * possible.length))
  ).join('')
}

/**
 * Checks if a number is in exponential notation
 * @param num - Number to check
 * @returns Boolean indicating if number is in exponential notation
 */
const isExponent = (num: number): boolean => {
  return num.toString().includes('e')
}

/**
 * Rounds a number to specified decimal places
 * @param num - Number to round
 * @param round - Number of decimal places (default: 2)
 * @returns Rounded number as string
 * @throws Error if input is not a valid number
 */
export const roundNum = (num: number, round: number = 2): string => {
  if (typeof num !== 'number' || isNaN(num)) {
    throw new Error('Input must be a valid number')
  }

  if (round < 0) {
    throw new Error('Round parameter must be non-negative')
  }

  if (isExponent(num)) {
    const [base, exponent] = num.toString().split('e')
    const roundedBase = parseFloat(base).toFixed(round)
    return parseFloat(`${roundedBase}e${exponent}`).toString()
  }

  return (Math.round(num * Math.pow(10, round)) / Math.pow(10, round))
    .toFixed(round)
}

/**
 * Converts a record's date string or Date object to a Date object
 * @param d - Object containing recordDate property
 * @returns Date object
 * @throws Error if recordDate is invalid
 */
export const getDate = (d?: DateRecord | null): Date => {
  if (!d?.recordDate) {
    throw new Error('Invalid date record');
  }

  const date = new Date(d.recordDate);

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date format');
  }

  return date;
};

/**
 * Checks if a number is a float
 * @param n - Number to check
 * @returns Boolean indicating if number is a float
 * @throws Error if input is not a valid number
 */
export const isFloat = (n: number): boolean => {
  if (typeof n !== 'number' || isNaN(n)) {
    throw new Error('Input must be a valid number')
  }

  return Number(n) === n && n % 1 !== 0
}

/**
 * Formats metric values based on formula and type
 * @param value - Number to format
 * @param isPct - Whether value is percentage
 * @param formula - Optional formula to apply
 * @returns Formatted string with optional percentage
 */
export const formatMetricValue = (
  value: number,
  isPct?: boolean,
  formula?: string
): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '-'
  }

  const sign = Math.sign(value)
  const absValue = Math.abs(value)

  if (isPct) {
    if (formula === "#val*100") {
      return `${roundNum(value * 100, 2)}%`
    }
    if (formula === "abs(#val)") {
      return `${roundNum(Math.abs(value), 2)}%`
    }
    const signedValue = sign * value
    if (Math.abs(signedValue) < 1 && signedValue !== 0) {
      return `${roundNum(signedValue, 2)}%`
    }
    return `${formatValue(signedValue)}%`
  }

  return formatValue(sign * value, true)
}


/**
 * Indian currency abbreviations
 */
export const COUNT_ABBRS = ['', 'K', 'L', 'Cr', 'kCr'] as const;

/**
 * Formats a number with Indian currency abbreviations
 * @param count - Number to format
 * @param withAbbr - Whether to include abbreviation suffix (default: false)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with optional abbreviation
 */
export const formatValue = (
  count: number,
  withAbbr: boolean = false,
  decimals: number = 2
): string => {
  // Handle invalid inputs
  if (count === undefined || count === null || isNaN(count)) {
    return '-';
  }

  // Handle zero specifically
  if (count === 0) return decimals > 0 ? '0.' + '0'.repeat(decimals) : '0';

  const absCount = Math.abs(count);

  // Handle small numbers
  if (absCount < 1) {
    return count.toFixed(decimals).replace(/\.?0+$/, '');
  }

  // Format according to Indian currency system
  let divisor = 1;
  let suffix = '';

  if (absCount >= 1e12) {
    divisor = 1e12;
    suffix = ' LCr'; // Lakh Crore
  } else if (absCount >= 1e10) {
    divisor = 1e10;
    suffix = ' kCr'; // Thousand Crore
  } else if (absCount >= 1e7) {
    divisor = 1e7;
    suffix = ' Cr'; // Crore
  } else if (absCount >= 1e5) {
    divisor = 1e5;
    suffix = ' L'; // Lakh
  } else if (absCount >= 1e3) {
    divisor = 1e3;
    suffix = ' K'; // Thousand
  } else {
    // Keep divisor as 1 for numbers < 1000
    divisor = 1;
    suffix = '';
  }

  // Format the number
  const formattedValue = (count / divisor).toFixed(decimals).replace(/\.?0+$/, '');

  // Split and format with commas using consistent Indian formatting
  const [whole, decimal] = formattedValue.split('.');
  const formattedWhole = whole.replace(/\B(?=(\d{2})+(\d{1})(?!\d))/g, ',');

  // Combine parts
  let result = decimal ? `${formattedWhole}.${decimal}` : formattedWhole;

  // Add abbreviation if requested
  if (withAbbr && suffix) {
    result += suffix;
  }

  return result;
}

/**
 * Formats a date string to 'MMM YY' format
 * @param dateStr - Date string to format
 * @returns Formatted date string
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: '2-digit'
  }).format(date);
}

/**
 * Formats a number to a compact string representation
 * @param num - Number to format
 * @returns Formatted compact number string
 */
export function formatCompactNumber(num: number): string {
  if (num === null || num === undefined || isNaN(num)) return '-';

  // Billion
  if (Math.abs(num) >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(2) + 'B';
  }
  // Million
  else if (Math.abs(num) >= 1_000_000) {
    return (num / 1_000_000).toFixed(2) + 'M';
  }
  // Thousand
  else if (Math.abs(num) >= 1_000) {
    return (num / 1_000).toFixed(2) + 'K';
  }
  // Regular number
  else {
    return num.toFixed(2);
  }
}

/**
 * Removes the leading ticker symbol from a financial metric string.
 * @param {string} input - The input string containing the ticker.
 * @returns {string} - The string without the ticker.
 */
export function removeTicker(input: string): string {
  return input.replace(/^[^.]+\./, '');
}


/**
 * Generates the URL for a company's avatar based on its ticker symbol.
 * Assumes logos are hosted at a specific base URL and follow a naming convention.
 * @param ticker - The stock ticker symbol (e.g., "RELIANCE").
 * @returns The full URL to the company's logo image.
 */
export const getAvatarUrl = (ticker: string): string => `https://logo.grufity.com/ind/${ticker}.png`;
